// evo/rooms/EvoRoomPromotions.tsx
// TEMPORARY: surfaces non-flash promotions on the room card until the final placement is decided.
// Each promotion renders like the cancellation line: a green "success" check icon, the promotion
// label in success colour, and an info button that opens a modal with the promotion description.
// CHILDREN_FREE_OFFER is intentionally excluded here — it renders as a "Kids stay free" badge at the
// top of the card instead (see EvoKidsStayFreeBadge).
import styled from 'styled-components';
import { useEvoModals } from '@/app/components/hotel-detail/evo/EvoModalsProvider';
import { CHILDREN_FREE_OFFER_PROMOTION_TYPE } from '@/app/components/hotel-detail/rooms/childrenFreeOffer';
import { groupPromotionsByLabel } from '@/app/components/hotel-detail/rooms/groupPromotionsByLabel';
import { getPromotionDisplayLabel } from '@/app/components/hotel-detail/rooms/promotionLabels';
import { promotionHasExtraInfo } from '@/app/components/hotel-detail/rooms/roomCommon';
import { useTranslation } from '@/app/components/translations/Translation';
import type { StarfishIconRef } from '@/app/common/icons/starfish';
import { EvoRoomInfoRow } from './EvoRoomInfoRow';

const PromotionsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: ${({ theme }) => theme.kallisto.space.space50};
`;

// Per-promotion-type leading icon (all from the green "success" set). Anything not listed
// falls back to the generic check icon.
const PROMOTION_ICON_BY_TYPE: Record<string, StarfishIconRef> = {
  TICKETS_INCLUDED: { category: 'Services', name: 'icon-starred-ticket' },
  SPA_INCLUDED: { category: 'Services', name: 'icon-wellness' },
  SKI_PASS_INCLUDED: { category: 'Services', name: 'icon-starred-ticket' },
  TRANSFER_INCLUDED: { category: 'Transportation', name: 'icon-bus' },
  RESORT_CREDIT: { category: 'Financial', name: 'icon-cash' },
};

const PROMOTION_FALLBACK_ICON: StarfishIconRef = { category: 'Semantics', name: 'icon-check' };

export function EvoRoomPromotions({ configurations }: { configurations: MealPlanType[] }) {
  const { openPromotionInfoModal } = useEvoModals();
  const { t } = useTranslation();

  const visiblePromotions = configurations.flatMap((config) =>
    (config.promotions ?? [])
      .filter(
        // promo.label is normalised in the rooms API route (falls back to the description); a promo
        // with neither would render an empty row, so it is skipped entirely.
        (promo) => promo.type !== 'FLASH_SALE' && promo.type !== CHILDREN_FREE_OFFER_PROMOTION_TYPE && !!promo.label
      )
      // Resolve the displayed label up front (MPH-721): grouping then collapses distinct provider
      // labels of the same mapped type into one row, and the info gate below compares descriptions
      // against the label the user actually sees — when the API route copied the description into
      // the label, the row shows the short result-page copy and the provider sentence stays
      // reachable in the info modal instead of being lost.
      .map((promo) => ({ ...promo, label: getPromotionDisplayLabel(promo, t) }))
  );

  // Group across configurations by label (MPH-719): suppliers reuse a standard label per promotion
  // code, so the same label can arrive several times — repeated across the group's rates or under
  // different types — with different descriptions. Each label renders one row and the info modal
  // lists every distinct description of the group.
  const promotionGroups = groupPromotionsByLabel(visiblePromotions);

  if (promotionGroups.length === 0) return null;

  const infoIcon = { category: 'Semantics', name: 'icon-circle-info', color: 'iconSuccess' } as const;

  return (
    <PromotionsSection>
      {promotionGroups.map((promotionGroup) => {
        const promotionIcon = {
          ...(PROMOTION_ICON_BY_TYPE[promotionGroup.type] ?? PROMOTION_FALLBACK_ICON),
          color: 'iconSuccess' as const,
        };
        const infoDescriptions = promotionGroup.descriptions.filter((description) =>
          promotionHasExtraInfo({ label: promotionGroup.label, description })
        );

        return (
          <EvoRoomInfoRow
            key={promotionGroup.label}
            icon={promotionIcon}
            label={promotionGroup.label}
            color="textSuccess"
            infoIcon={infoDescriptions.length > 0 ? infoIcon : undefined}
            onInfoClick={() => openPromotionInfoModal({ ...promotionGroup, descriptions: infoDescriptions })}
          />
        );
      })}
    </PromotionsSection>
  );
}
